// Composant de progression des badges dynamique basé sur la table badge_types

import React, { useState, useEffect } from 'react';
import {Target, Trophy, Award, Star, Medal, Crown, Shield, Users} from 'lucide-react';
import { supabase } from '../services/supabaseClient';

interface BadgeType {
    id: number;
    name: string;
    description: string;
    icon?: string;
    requirement_type: string;
    requirement_value: number;
    color: string;
}

interface BadgeProgressionProps {
    memberData: {
        referrals_count: number;
        badges: string[];
    };
}

const BadgeProgression: React.FC<BadgeProgressionProps> = ({ memberData }) => {
    const [badgeTypes, setBadgeTypes] = useState<BadgeType[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Récupérer les types de badges depuis la base
    useEffect(() => {
        fetchBadgeTypes();
    }, []);

    // Fonction pour obtenir l'icône Lucide selon le nom du badge
    const getBadgeIcon = (badgeName: string, color: string, className?: string) => {
        const iconProps = {
            className: className || 'w-6 h-6',
            style: { color: getBadgeIconColor(color) }
        };

        switch (badgeName.toLowerCase()) {
            case 'pionnier':
                return <Star {...iconProps} />;
            case 'recruteur':
                return <Users {...iconProps} />;
            case 'bâtisseur or':
                return <Medal {...iconProps} />;
            case 'ambassadeur':
                return <Shield {...iconProps} />;
            case 'légende':
                return <Crown {...iconProps} />;
            default:
                return <Trophy {...iconProps} />;
        }
    };

    // Fonction pour obtenir la couleur CSS de l'icône
    const getBadgeIconColor = (color: string): string => {
        const colorMap: { [key: string]: string } = {
            'blue': '#2563eb',
            'yellow': '#eab308',
            'orange': '#ea580c',
            'purple': '#9333ea',
            'red': '#dc2626',
            'green': '#16a34a',
            'indigo': '#4f46e5',
            'pink': '#ec4899'
        };
        return colorMap[color] || '#6b7280';
    };

    const fetchBadgeTypes = async () => {
        try {
            // Utiliser Supabase pour récupérer les badge_types
            const { data, error } = await supabase
                .from('badge_types')
                .select('*')
                .eq('requirement_type', 'referrals')
                .order('requirement_value', { ascending: true });

            if (error) {
                throw error;
            }

            setBadgeTypes(data || []);
        } catch (err: any) {
            console.error('Erreur récupération badge_types:', err);
            setError('Erreur de chargement des badges');

            // Fallback avec les badges hardcodés si erreur
            setBadgeTypes([
                { id: 1, name: 'Recruteur', description: 'Parrainage de 5 nouveaux membres', requirement_type: 'referrals', requirement_value: 5, color: 'blue' },
                { id: 2, name: 'Bâtisseur Or', description: 'Parrainage de 25 nouveaux membres', requirement_type: 'referrals', requirement_value: 25, color: 'yellow' },
                { id: 3, name: 'Ambassadeur', description: 'Parrainage de 50 nouveaux membres', requirement_type: 'referrals', requirement_value: 50, color: 'purple' },
                { id: 4, name: 'Légende', description: 'Parrainage de 100 nouveaux membres', requirement_type: 'referrals', requirement_value: 100, color: 'red' }
            ]);
        } finally {
            setLoading(false);
        }
    };

    // Trouver le prochain badge à obtenir
    const getNextBadge = (): BadgeType | null => {
        // CORRECTION : S'assurer que referrals_count est un nombre
        const currentReferrals = Number(memberData.referrals_count) || 0;

        const nextBadge = badgeTypes.find(badge =>
            currentReferrals < badge.requirement_value
        );

        return nextBadge || null;
    };

    // Obtenir la couleur Tailwind selon la couleur du badge
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const getBadgeColor = (color: string): string => {
        const colorMap: { [key: string]: string } = {
            'blue': 'bg-blue-600',
            'yellow': 'bg-yellow-600',
            'orange': 'bg-orange-600',
            'purple': 'bg-purple-600',
            'red': 'bg-red-600',
            'green': 'bg-green-600',
            'indigo': 'bg-indigo-600',
            'pink': 'bg-pink-600'
        };
        return colorMap[color] || 'bg-gray-600';
    };

    // Obtenir tous les badges débloqués
    const getUnlockedBadges = (): BadgeType[] => {
        const currentReferrals = Number(memberData.referrals_count) || 0;

        return badgeTypes.filter(badge =>
            currentReferrals >= badge.requirement_value
        );
    };

    // Calculer le statut actuel
    const getCurrentStatus = (): { name: string; icon: React.ReactNode } => {
        const unlockedBadges = getUnlockedBadges();

        if (unlockedBadges.length === 0) {
            return { name: 'Membre', icon: <Target className="w-6 h-6 text-gray-600" /> };
        }

        const highestBadge = unlockedBadges[unlockedBadges.length - 1];
        return {
            name: highestBadge.name,
            icon: getBadgeIcon(highestBadge.name, highestBadge.color)
        };
    };

    if (loading) {
        return (
            <div className="bg-gray-50 p-6 rounded-xl">
                <div className="flex items-center space-x-2 mb-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <span className="text-gray-600">Chargement des badges...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 p-6 rounded-xl">
                <p className="text-red-800 text-sm">{error}</p>
            </div>
        );
    }

    const nextBadge = getNextBadge();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const currentStatus = getCurrentStatus();
    const unlockedBadges = getUnlockedBadges();

    const currentReferrals = Number(memberData.referrals_count) || 0;

    return (
        <div className="space-y-6">
            {/* Statut actuel compact */}

            {/* Progression vers le prochain badge - Design simple et beau */}
            {nextBadge && (
                <div className="bg-white border border-gray-200 shadow-sm">
                    <div className="bg-blue-600 text-white p-3">
                        <div className="flex items-center space-x-2">
                            <Target className="w-5 h-5" />
                            <h4 className="font-semibold text-lg">Prochain badge</h4>
                        </div>
                    </div>

                    <div className="p-6">
                        {/* Badge info */}
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center space-x-4">
                                <div className="w-12 h-12 bg-gray-100 flex items-center justify-center">
                                    {getBadgeIcon(nextBadge.name, nextBadge.color, "w-6 h-6 text-gray-700")}
                                </div>
                                <div>
                                    <h5 className="font-bold text-xl text-gray-900">{nextBadge.name}</h5>
                                    <p className="text-gray-600">{nextBadge.description}</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="bg-gray-100 px-4 py-2 font-bold text-gray-900">
                                    {currentReferrals}/{nextBadge.requirement_value}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">Progression</p>
                            </div>
                        </div>

                        {/* Barre de progression simple */}
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="font-semibold text-gray-900">Progression</span>
                                <span className="font-bold text-blue-600">
                                    {Math.round((currentReferrals / nextBadge.requirement_value) * 100)}%
                                </span>
                            </div>

                            <div className="bg-gray-200 h-2">
                                <div
                                    className="bg-blue-600 h-2 transition-all duration-700 ease-out"
                                    style={{
                                        width: `${Math.min((currentReferrals / nextBadge.requirement_value) * 100, 100)}%`
                                    }}
                                />
                            </div>
                        </div>

                        {/* Message */}
                        <div className="mt-6 bg-blue-50 p-4 border-l-4 border-blue-600">
                            <p className="text-gray-900">
                                <span className="font-bold text-blue-600">
                                    {Math.max(0, nextBadge.requirement_value - currentReferrals)}
                                </span>
                                {' '}parrainage{(nextBadge.requirement_value - currentReferrals) > 1 ? 's' : ''} restant{(nextBadge.requirement_value - currentReferrals) > 1 ? 's' : ''} pour obtenir
                                <span className="font-bold"> {nextBadge.name}</span>
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Message si tous les badges sont débloqués */}
            {!nextBadge && badgeTypes.length > 0 && (
                <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-6 rounded-xl border border-purple-200">
                    <div className="text-center">
                        <div className="flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-lg mx-auto mb-3">
                            <Crown className="w-8 h-8 text-purple-600" />
                        </div>
                        <h4 className="font-bold text-purple-800 text-lg">Félicitations !</h4>
                        <p className="text-purple-600 mt-2">
                            Vous avez débloqué tous les badges disponibles ! Vous êtes une légende UDB !
                        </p>
                        <p className="text-sm text-purple-500 mt-1">
                            {currentReferrals} parrainages effectués
                        </p>
                    </div>
                </div>
            )}

            {/* Badges débloqués */}
            {unlockedBadges.length > 0 && (
                <div>
                    <div className="flex items-center space-x-3 mb-4">
                        <div className="w-5 h-5 bg-green-600 flex items-center justify-center">
                            <Trophy className="w-3 h-3 text-white" />
                        </div>
                        <h4 className="font-semibold text-gray-900">
                            Badges débloqués ({unlockedBadges.length})
                        </h4>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {unlockedBadges.map((badge) => (
                            <div
                                key={badge.id}
                                className="bg-white p-4 border border-gray-200 shadow-sm"
                            >
                                <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-green-100 flex items-center justify-center">
                                        {getBadgeIcon(badge.name, badge.color, "w-5 h-5 text-green-600")}
                                    </div>
                                    <div className="flex-1">
                                        <h5 className="font-semibold text-gray-900">{badge.name}</h5>
                                        <p className="text-sm text-gray-600">{badge.description}</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Requis: {badge.requirement_value} parrainage{badge.requirement_value > 1 ? 's' : ''}
                                        </p>
                                    </div>
                                    <div className="w-6 h-6 bg-green-100 flex items-center justify-center">
                                        <Award className="w-4 h-4 text-green-600" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Aperçu de tous les badges disponibles */}
            <div>
                <div className="flex items-center space-x-3 mb-4">
                    <div className="w-5 h-5 bg-gray-600 flex items-center justify-center">
                        <Trophy className="w-3 h-3 text-white" />
                    </div>
                    <h4 className="font-semibold text-gray-900">Tous les badges disponibles</h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {badgeTypes.map((badge) => {
                        const isUnlocked = currentReferrals >= badge.requirement_value;
                        const isNext = nextBadge?.id === badge.id;

                        return (
                            <div
                                key={badge.id}
                                className={`bg-white p-3 border transition-all ${
                                    isUnlocked
                                        ? 'border-green-600 bg-green-50'
                                        : isNext
                                            ? 'border-blue-600 bg-blue-50'
                                            : 'border-gray-200'
                                }`}
                            >
                                <div className="flex items-center space-x-3">
                                    <div className={`w-8 h-8 flex items-center justify-center ${
                                        isUnlocked ? 'bg-green-100' : isNext ? 'bg-blue-100' : 'bg-gray-100'
                                    }`}>
                                        {getBadgeIcon(badge.name, badge.color, `w-4 h-4 ${
                                            isUnlocked ? 'text-green-600' : isNext ? 'text-blue-600' : 'text-gray-500'
                                        }`)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h6 className={`font-medium text-sm ${
                                            isUnlocked ? 'text-green-900' : isNext ? 'text-blue-900' : 'text-gray-600'
                                        }`}>
                                            {badge.name}
                                        </h6>
                                        <p className="text-xs text-gray-500">
                                            {badge.requirement_value} parrainage{badge.requirement_value > 1 ? 's' : ''}
                                        </p>
                                    </div>
                                    {isUnlocked && (
                                        <div className="w-4 h-4 bg-green-100 flex items-center justify-center">
                                            <Award className="w-3 h-3 text-green-600" />
                                        </div>
                                    )}
                                    {isNext && (
                                        <div className="w-4 h-4 bg-blue-100 flex items-center justify-center">
                                            <Target className="w-3 h-3 text-blue-600" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export const initializeDefaultBadges = async () => {
    try {
        const { data: existingBadges } = await supabase
            .from('badge_types')
            .select('name')
            .eq('requirement_type', 'referrals');

        if (!existingBadges || existingBadges.length === 0) {
            const defaultBadges = [
                {
                    name: 'Pionnier',
                    description: 'Badge de membre fondateur',
                    requirement_type: 'default',
                    requirement_value: 0,
                    color: 'blue'
                },
                {
                    name: 'Recruteur',
                    description: 'Premier succès en parrainage',
                    requirement_type: 'referrals',
                    requirement_value: 5,
                    color: 'blue'
                },
                {
                    name: 'Bâtisseur Or',
                    description: 'Parrainage confirmé et actif',
                    requirement_type: 'referrals',
                    requirement_value: 25,
                    color: 'yellow'
                },
                {
                    name: 'Ambassadeur',
                    description: 'Excellent recruteur de l\'Union',
                    requirement_type: 'referrals',
                    requirement_value: 50,
                    color: 'purple'
                },
                {
                    name: 'Légende',
                    description: 'Statut légendaire UDB',
                    requirement_type: 'referrals',
                    requirement_value: 100,
                    color: 'red'
                }
            ];

            const { error } = await supabase
                .from('badge_types')
                .insert(defaultBadges);

            if (error) {
                console.error('Erreur insertion badges par défaut:', error);
            } else {
                console.log('✅ Badges par défaut initialisés avec les nouveaux seuils: 5, 25, 50, 100');
            }
        }
    } catch (error) {
        console.error('Erreur initialisation badges:', error);
    }
};

export default BadgeProgression;
